<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Modal from '../../../../components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PipelineFollowUpRulesAPI from 'dashboard/api/pipelineFollowUpRules';
import SendWindowFields from 'dashboard/components-next/follow-ups/SendWindowFields.vue';
import SendWindowBadge from 'dashboard/components-next/follow-ups/SendWindowBadge.vue';
import { isSendWindowValid } from 'dashboard/composables/useSendWindow';
import Select from 'dashboard/components-next/select/Select.vue';
import ConfirmModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  pipeline: {
    type: Object,
    required: false,
    default: null,
  },
});

const emit = defineEmits(['close']);
const store = useStore();
const route = useRoute();
const { t } = useI18n();
const { showAlert } = useAlert();

// FU-32: a recusa do backend (ex.: modo IA sem chave OpenAI) diz o que corrigir;
// o "erro" genérico escondia o motivo e a regra parecia salvar à toa.
const saveErrorMessage = error =>
  error?.response?.data?.errors?.join(', ') ||
  error?.response?.data?.error ||
  t('FOLLOW_UP_RULES.MESSAGES.ERROR');

const isLoading = ref(true);
const isSaving = ref(false);
const loadError = ref(false);
const dependencyLoadError = ref(false);
const deletingId = ref(null);
const togglingId = ref(null);
const rules = ref([]);
const showForm = ref(false);
const editingRule = ref(null);
const initialFormSnapshot = ref('');
const validationError = ref('');
const discardConfirmation = ref(null);
const deleteConfirmation = ref(null);
const pendingDelete = ref(null);
let requestSequence = 0;
let deleteOperationSequence = 0;
let mutationSequence = 0;

const normalizeContextId = value => {
  if (value === undefined || value === null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};
const currentAccountId = computed(() =>
  normalizeContextId(store.getters.getCurrentAccountId)
);
const capturePipelineContext = () => ({
  accountId: currentAccountId.value,
  pipelineId: normalizeContextId(props.pipeline?.id),
});
const pipelineContextIsCurrent = context =>
  Boolean(
    props.show &&
      context.accountId !== null &&
      context.pipelineId !== null &&
      context.accountId === currentAccountId.value &&
      context.pipelineId === normalizeContextId(props.pipeline?.id)
  );
const beginMutationContext = () => {
  mutationSequence += 1;
  return {
    ...capturePipelineContext(),
    sequence: mutationSequence,
  };
};
const mutationContextIsCurrent = context =>
  context.sequence === mutationSequence && pipelineContextIsCurrent(context);

const form = ref({
  follow_up_template_id: null,
  sender_id: null,
  from_stage: '',
  to_stage: '',
  delay_minutes: 0,
  delay_unit: 'minutes',
  enabled: true,
  send_window: {},
});

// Get stages from pipeline
const stages = computed(() => {
  if (!props.pipeline?.stages) return [];
  return Object.entries(props.pipeline.stages)
    .map(([key, value]) => ({
      key,
      name: value.name || key,
      position: Number(value.position) || 0,
    }))
    .sort((a, b) => a.position - b.position);
});

// Get templates from store
const templates = computed(
  () => store.getters['followUpTemplates/getTemplates'] || []
);

// Agents (account-scoped by the /agents endpoint) for the optional sender field.
const agents = computed(() =>
  (store.getters['agents/getAgents'] || []).filter(agent => {
    const agentAccountId = normalizeContextId(agent.account_id);
    return agentAccountId === null || agentAccountId === currentAccountId.value;
  })
);

const normalizeReference = value => normalizeContextId(value) || '';
const withStaleOption = (options, currentValue) => {
  const normalizedCurrentValue = normalizeReference(currentValue);
  if (
    !normalizedCurrentValue ||
    options.some(
      option => normalizeReference(option.value) === normalizedCurrentValue
    )
  ) {
    return options;
  }

  return [
    {
      value: currentValue,
      label: t('PIPELINE.LABEL_MAPPINGS.DELETED_ITEM', {
        name: String(currentValue),
      }),
    },
    ...options,
  ];
};

const templateOptions = computed(() =>
  withStaleOption(
    templates.value.map(template => ({
      value: template.id,
      label: template.name,
    })),
    form.value.follow_up_template_id
  )
);

const agentOptions = computed(() =>
  withStaleOption(
    [
      { value: '', label: t('FOLLOW_UP_RULES.FORM.SENDER.DEFAULT') },
      ...agents.value.map(agent => ({ value: agent.id, label: agent.name })),
    ],
    form.value.sender_id
  )
);

const fromStageOptions = computed(() =>
  withStaleOption(
    [
      { value: '', label: t('FOLLOW_UP_RULES.FORM.ANY_STAGE') },
      ...stages.value.map(stage => ({ value: stage.key, label: stage.name })),
    ],
    form.value.from_stage
  )
);

const toStageOptions = computed(() =>
  withStaleOption(
    stages.value.map(stage => ({ value: stage.key, label: stage.name })),
    form.value.to_stage
  )
);

const delayUnitOptions = computed(() => [
  {
    value: 'minutes',
    label: t('FOLLOW_UP_RULES.FORM.MINUTES'),
  },
  {
    value: 'hours',
    label: t('FOLLOW_UP_RULES.FORM.HOURS'),
  },
  {
    value: 'days',
    label: t('FOLLOW_UP_RULES.FORM.DAYS'),
  },
]);

const isFormDirty = computed(
  () => JSON.stringify(form.value) !== initialFormSnapshot.value
);

const fetchRules = async context => {
  const response = await PipelineFollowUpRulesAPI.get(
    context.pipelineId,
    context.accountId
  );
  return response.data.payload || [];
};

const fetchTemplates = context =>
  store.dispatch('followUpTemplates/fetch', {
    accountId: context.accountId,
    generation: requestSequence,
  });

const fetchAgents = context =>
  store.dispatch('agents/get', { accountId: context.accountId });

const loadData = async () => {
  requestSequence += 1;
  const sequence = requestSequence;
  const context = capturePipelineContext();
  isLoading.value = true;
  loadError.value = false;
  dependencyLoadError.value = false;

  if (context.accountId === null || context.pipelineId === null) {
    rules.value = [];
    loadError.value = true;
    isLoading.value = false;
    return;
  }

  const [rulesResult, templatesResult, agentsResult] = await Promise.allSettled(
    [fetchRules(context), fetchTemplates(context), fetchAgents(context)]
  );
  if (sequence !== requestSequence || !pipelineContextIsCurrent(context)) {
    return;
  }

  if (rulesResult.status === 'fulfilled') {
    rules.value = rulesResult.value;
  } else {
    rules.value = [];
    loadError.value = true;
  }

  dependencyLoadError.value =
    templatesResult.status === 'rejected' || agentsResult.status === 'rejected';
  isLoading.value = false;
};

// Fetch rules when modal opens
watch(
  [
    () => props.show,
    () => props.pipeline?.id,
    () => store.getters.getCurrentAccountId,
  ],
  (
    [show, pipelineId, accountId],
    [previousShow, previousPipelineId, previousAccountId] = []
  ) => {
    const normalizedPipelineId = normalizeContextId(pipelineId);
    const normalizedPreviousPipelineId = normalizeContextId(previousPipelineId);
    const normalizedAccountId = normalizeContextId(accountId);
    const normalizedPreviousAccountId = normalizeContextId(previousAccountId);
    const contextChanged =
      previousShow !== undefined &&
      (show !== previousShow ||
        normalizedPipelineId !== normalizedPreviousPipelineId ||
        normalizedAccountId !== normalizedPreviousAccountId);
    if (contextChanged) {
      requestSequence += 1;
      deleteOperationSequence += 1;
      mutationSequence += 1;
      pendingDelete.value = null;
      deletingId.value = null;
      togglingId.value = null;
      isSaving.value = false;
    }

    if (show) {
      if (
        previousShow &&
        normalizedPreviousPipelineId &&
        (normalizedPipelineId !== normalizedPreviousPipelineId ||
          normalizedAccountId !== normalizedPreviousAccountId)
      ) {
        showForm.value = false;
        editingRule.value = null;
        rules.value = [];
        validationError.value = '';
        initialFormSnapshot.value = '';
      }
      loadData();
    } else requestSequence += 1;
  },
  { immediate: true }
);

function resetForm() {
  form.value = {
    follow_up_template_id: templates.value[0]?.id || '',
    sender_id: '',
    from_stage: '',
    to_stage: stages.value[0]?.key || '',
    delay_minutes: 0,
    delay_unit: 'minutes',
    enabled: true,
    send_window: {},
  };
  validationError.value = '';
  initialFormSnapshot.value = JSON.stringify(form.value);
}

const closeModal = () => {
  showForm.value = false;
  editingRule.value = null;
  resetForm();
  emit('close');
};

const handleClose = async () => {
  if (isSaving.value || deletingId.value !== null || togglingId.value !== null)
    return;
  if (showForm.value && isFormDirty.value) {
    const confirmed = await discardConfirmation.value.showConfirmation();
    if (!confirmed) return;
  }
  closeModal();
};

const closeForm = async () => {
  if (isSaving.value) return;
  if (isFormDirty.value) {
    const confirmed = await discardConfirmation.value.showConfirmation();
    if (!confirmed) return;
  }
  showForm.value = false;
  editingRule.value = null;
  resetForm();
};

const openCreateForm = () => {
  editingRule.value = null;
  resetForm();
  showForm.value = true;
};

const openEditForm = rule => {
  editingRule.value = rule;

  // Parse delay into value and unit
  let delayValue = rule.delay_minutes;
  let delayUnit = 'minutes';

  if (delayValue >= 1440 && delayValue % 1440 === 0) {
    delayValue /= 1440;
    delayUnit = 'days';
  } else if (delayValue >= 60 && delayValue % 60 === 0) {
    delayValue /= 60;
    delayUnit = 'hours';
  }

  form.value = {
    follow_up_template_id: rule.follow_up_template_id,
    sender_id: rule.sender_id || '',
    from_stage: rule.from_stage || '',
    to_stage: rule.to_stage,
    delay_minutes: delayValue,
    delay_unit: delayUnit,
    enabled: rule.enabled,
    send_window: { ...(rule.send_window || {}) },
  };
  validationError.value = '';
  initialFormSnapshot.value = JSON.stringify(form.value);
  showForm.value = true;
};

const calculateDelayMinutes = () => {
  const value = Number.parseInt(form.value.delay_minutes, 10) || 0;
  switch (form.value.delay_unit) {
    case 'hours':
      return value * 60;
    case 'days':
      return value * 1440;
    default:
      return value;
  }
};

const handleSubmit = async () => {
  if (isSaving.value) return;
  validationError.value = '';

  const stageKeys = new Set(stages.value.map(stage => String(stage.key)));
  const templateById = new Map(
    templates.value.map(template => [normalizeReference(template.id), template])
  );
  const agentById = new Map(
    agents.value.map(agent => [normalizeReference(agent.id), agent])
  );
  const fromStage = normalizeReference(form.value.from_stage);
  const toStage = normalizeReference(form.value.to_stage);
  const templateId = normalizeReference(form.value.follow_up_template_id);
  const senderId = normalizeReference(form.value.sender_id);
  const delayMinutes = calculateDelayMinutes();
  if (
    !stageKeys.has(toStage) ||
    (fromStage && !stageKeys.has(fromStage)) ||
    !templateById.has(templateId) ||
    (senderId && !agentById.has(senderId)) ||
    (fromStage && fromStage === toStage) ||
    !Number.isSafeInteger(delayMinutes) ||
    delayMinutes < 0
  ) {
    validationError.value = t('FOLLOW_UP_RULES.MESSAGES.VALIDATION_ERROR');
    return;
  }

  if (!isSendWindowValid(form.value.send_window)) {
    validationError.value = t('FOLLOW_UP_SEND_WINDOW.INVALID');
    return;
  }

  const context = beginMutationContext();
  if (context.accountId === null || context.pipelineId === null) {
    validationError.value = t('FOLLOW_UP_RULES.MESSAGES.VALIDATION_ERROR');
    return;
  }
  const editingRuleId = editingRule.value?.id || null;
  isSaving.value = true;

  const data = {
    follow_up_template_id: templateById.get(templateId).id,
    sender_id: senderId ? agentById.get(senderId).id : null,
    from_stage: fromStage || null,
    to_stage: toStage,
    delay_minutes: delayMinutes,
    enabled: form.value.enabled,
    send_window: form.value.send_window,
  };

  try {
    if (!mutationContextIsCurrent(context)) return;
    if (editingRuleId) {
      await PipelineFollowUpRulesAPI.update(
        context.pipelineId,
        editingRuleId,
        data,
        context.accountId
      );
      if (!mutationContextIsCurrent(context)) return;
      showAlert(t('FOLLOW_UP_RULES.MESSAGES.UPDATE_SUCCESS'));
    } else {
      await PipelineFollowUpRulesAPI.create(
        context.pipelineId,
        data,
        context.accountId
      );
      if (!mutationContextIsCurrent(context)) return;
      showAlert(t('FOLLOW_UP_RULES.MESSAGES.CREATE_SUCCESS'));
    }

    showForm.value = false;
    editingRule.value = null;
    resetForm();
    await loadData();
  } catch (error) {
    if (mutationContextIsCurrent(context)) {
      showAlert(saveErrorMessage(error));
    }
  } finally {
    if (mutationContextIsCurrent(context)) isSaving.value = false;
  }
};

const handleDelete = async rule => {
  if (deletingId.value !== null || pendingDelete.value !== null) return;
  const context = capturePipelineContext();
  if (context.accountId === null || context.pipelineId === null) return;
  const ruleId = rule.id;
  deleteOperationSequence += 1;
  const operationSequence = deleteOperationSequence;
  const contextStillMatches = () =>
    operationSequence === deleteOperationSequence &&
    pipelineContextIsCurrent(context);

  pendingDelete.value = rule;
  const confirmed = await deleteConfirmation.value.showConfirmation();
  if (!contextStillMatches()) return;
  if (!confirmed) {
    pendingDelete.value = null;
    return;
  }

  deletingId.value = ruleId;
  try {
    if (!contextStillMatches()) return;
    await PipelineFollowUpRulesAPI.delete(
      context.pipelineId,
      ruleId,
      context.accountId
    );
    if (!contextStillMatches()) return;
    showAlert(t('FOLLOW_UP_RULES.MESSAGES.DELETE_SUCCESS'));
    await loadData();
  } catch {
    if (contextStillMatches()) {
      showAlert(t('FOLLOW_UP_RULES.MESSAGES.ERROR'));
    }
  } finally {
    if (operationSequence === deleteOperationSequence) {
      deletingId.value = null;
      pendingDelete.value = null;
    }
  }
};

const toggleEnabled = async rule => {
  if (togglingId.value !== null) return;
  const context = beginMutationContext();
  if (context.accountId === null || context.pipelineId === null) return;
  const ruleId = rule.id;
  togglingId.value = ruleId;
  try {
    if (!mutationContextIsCurrent(context)) return;
    await PipelineFollowUpRulesAPI.update(
      context.pipelineId,
      ruleId,
      { enabled: !rule.enabled },
      context.accountId
    );
    if (!mutationContextIsCurrent(context)) return;
    await loadData();
  } catch (error) {
    if (mutationContextIsCurrent(context)) {
      showAlert(saveErrorMessage(error));
    }
  } finally {
    if (mutationContextIsCurrent(context) && togglingId.value === ruleId) {
      togglingId.value = null;
    }
  }
};

const getStageName = stageKey => {
  const stage = stages.value.find(s => s.key === stageKey);
  return stage?.name || stageKey;
};

const getDelayDescription = rule => {
  const minutes = rule.delay_minutes || 0;
  if (minutes === 0) return t('FOLLOW_UP_RULES.FORM.DELAY_HINT');

  if (minutes < 60) {
    return t('FOLLOW_UP_RULES.LIST.MINUTES', { count: minutes });
  }
  if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    return t('FOLLOW_UP_RULES.LIST.HOURS', { count: hours });
  }
  const days = Math.floor(minutes / 1440);
  return t('FOLLOW_UP_RULES.LIST.DAYS', { count: days });
};
</script>

<template>
  <Modal
    :show="show"
    size="large"
    aria-labelledby="pipeline-follow-up-rules-title"
    :close-on-escape="!isSaving && deletingId === null && togglingId === null"
    @close="handleClose"
  >
    <div class="flex max-h-[85dvh] flex-col">
      <!-- Header -->
      <header class="flex-shrink-0 px-4 pt-4 pb-3">
        <h3
          id="pipeline-follow-up-rules-title"
          class="text-lg font-semibold text-n-slate-12"
        >
          {{ t('FOLLOW_UP_RULES.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mt-0.5">
          {{ t('FOLLOW_UP_RULES.DESCRIPTION') }}
        </p>
      </header>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-4">
        <!-- Loading -->
        <div
          v-if="isLoading"
          role="status"
          class="flex items-center justify-center gap-2 py-8"
        >
          <span class="i-svg-spinners-90-ring-with-bg size-6 text-n-brand" />
          <span class="sr-only">{{ t('FOLLOW_UP_RULES.LOADING') }}</span>
        </div>

        <div
          v-else-if="loadError"
          role="alert"
          class="flex flex-col items-center gap-3 rounded-xl border border-n-ruby-8 bg-n-ruby-3 p-6 text-center"
        >
          <p class="text-sm text-n-ruby-11">
            {{ t('FOLLOW_UP_RULES.MESSAGES.LOAD_ERROR') }}
          </p>
          <NextButton
            type="button"
            variant="outline"
            color="ruby"
            size="md"
            @click="loadData"
          >
            {{ t('FOLLOW_UP_RULES.RETRY') }}
          </NextButton>
        </div>

        <template v-else>
          <div
            v-if="dependencyLoadError"
            role="alert"
            class="mb-4 flex flex-col items-start gap-3 rounded-lg border border-n-amber-8 bg-n-amber-3 p-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <span class="text-sm text-n-amber-11">
              {{ t('FOLLOW_UP_RULES.MESSAGES.DEPENDENCY_LOAD_ERROR') }}
            </span>
            <button
              type="button"
              class="min-h-11 rounded-lg px-3 text-sm font-medium text-n-amber-11 outline outline-1 outline-n-amber-8 hover:bg-n-amber-4"
              @click="loadData"
            >
              {{ t('FOLLOW_UP_RULES.RETRY') }}
            </button>
          </div>

          <!-- Form -->
          <form
            v-else-if="showForm"
            class="space-y-4"
            @submit.prevent="handleSubmit"
          >
            <div class="p-4 bg-n-alpha-2 rounded-lg border border-n-weak">
              <h4 class="text-sm font-medium text-n-slate-12 mb-4">
                <template v-if="editingRule">
                  {{ t('FOLLOW_UP_RULES.FORM.EDIT_TITLE') }}
                </template>
                <template v-else>
                  {{ t('FOLLOW_UP_RULES.FORM.CREATE_TITLE') }}
                </template>
              </h4>

              <div
                v-if="validationError"
                role="alert"
                class="mb-4 rounded-lg border border-n-ruby-8 bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
              >
                {{ validationError }}
              </div>

              <!-- Template Selection -->
              <div class="mb-4">
                <label
                  for="pipeline-follow-up-template"
                  class="block text-sm font-medium text-n-slate-11 mb-1.5"
                >
                  {{ t('FOLLOW_UP_RULES.FORM.TEMPLATE') }}
                </label>
                <div
                  v-if="templates.length === 0"
                  class="p-3 border border-n-amber rounded-lg bg-n-amber-3"
                >
                  <p class="text-sm text-n-amber-11">
                    {{ t('FOLLOW_UP_RULES.FORM.NO_TEMPLATES') }}
                  </p>
                  <a
                    :href="`/app/accounts/${currentAccountId || route.params.accountId}/follow-ups/templates`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-sm font-medium text-n-brand hover:underline mt-1 inline-block"
                  >
                    {{ t('FOLLOW_UP_RULES.FORM.CREATE_TEMPLATE') }}
                  </a>
                </div>
                <Select
                  v-else
                  id="pipeline-follow-up-template-select"
                  v-model="form.follow_up_template_id"
                  input-id="pipeline-follow-up-template"
                  name="pipelineFollowUpTemplate"
                  class="w-full [&>select]:min-h-11 [&>select]:w-full"
                  :aria-label="t('FOLLOW_UP_RULES.FORM.TEMPLATE')"
                  :placeholder="t('FOLLOW_UP_RULES.FORM.SELECT_TEMPLATE')"
                  :options="templateOptions"
                  :disabled="isSaving"
                />
              </div>

              <!-- Sender (optional agent that authors the follow-up message) -->
              <div class="mb-4">
                <label
                  for="pipeline-follow-up-sender"
                  class="block text-sm font-medium text-n-slate-11 mb-1.5"
                >
                  {{ t('FOLLOW_UP_RULES.FORM.SENDER.LABEL') }}
                </label>
                <Select
                  id="pipeline-follow-up-sender-select"
                  v-model="form.sender_id"
                  input-id="pipeline-follow-up-sender"
                  name="pipelineFollowUpSender"
                  class="w-full [&>select]:min-h-11 [&>select]:w-full"
                  :aria-label="t('FOLLOW_UP_RULES.FORM.SENDER.LABEL')"
                  :options="agentOptions"
                  :disabled="isSaving"
                />
                <p class="mt-1.5 text-xs text-n-slate-10">
                  {{ t('FOLLOW_UP_RULES.FORM.SENDER.HINT') }}
                </p>
              </div>

              <!-- Stage Transition -->
              <div class="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-2">
                <div>
                  <label
                    for="pipeline-follow-up-from-stage"
                    class="block text-sm font-medium text-n-slate-11 mb-1.5"
                  >
                    {{ t('FOLLOW_UP_RULES.FORM.FROM_STAGE') }}
                  </label>
                  <Select
                    id="pipeline-follow-up-from-stage-select"
                    v-model="form.from_stage"
                    input-id="pipeline-follow-up-from-stage"
                    name="pipelineFollowUpFromStage"
                    class="w-full [&>select]:min-h-11 [&>select]:w-full"
                    :aria-label="t('FOLLOW_UP_RULES.FORM.FROM_STAGE')"
                    :options="fromStageOptions"
                    :disabled="isSaving"
                  />
                </div>
                <div>
                  <label
                    for="pipeline-follow-up-to-stage"
                    class="block text-sm font-medium text-n-slate-11 mb-1.5"
                  >
                    {{ t('FOLLOW_UP_RULES.FORM.TO_STAGE') }}
                  </label>
                  <Select
                    id="pipeline-follow-up-to-stage-select"
                    v-model="form.to_stage"
                    input-id="pipeline-follow-up-to-stage"
                    name="pipelineFollowUpToStage"
                    class="w-full [&>select]:min-h-11 [&>select]:w-full"
                    :aria-label="t('FOLLOW_UP_RULES.FORM.TO_STAGE')"
                    :options="toStageOptions"
                    :disabled="isSaving"
                  />
                  <p
                    v-if="form.from_stage === form.to_stage"
                    class="mt-1.5 text-xs text-n-ruby-11"
                  >
                    {{ t('FOLLOW_UP_RULES.FORM.STAGES_MUST_DIFFER') }}
                  </p>
                </div>
              </div>

              <!-- Delay -->
              <div class="mb-4">
                <span
                  id="pipeline-follow-up-delay-label"
                  class="block text-sm font-medium text-n-slate-11 mb-1.5"
                >
                  {{ t('FOLLOW_UP_RULES.FORM.DELAY') }}
                </span>
                <div
                  role="group"
                  aria-labelledby="pipeline-follow-up-delay-label"
                  class="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center"
                >
                  <input
                    id="pipeline-follow-up-delay"
                    v-model.number="form.delay_minutes"
                    name="pipelineFollowUpDelay"
                    type="number"
                    min="0"
                    step="1"
                    inputmode="numeric"
                    :disabled="isSaving"
                    :aria-label="t('FOLLOW_UP_RULES.FORM.DELAY_AMOUNT_LABEL')"
                    :placeholder="t('FOLLOW_UP_RULES.FORM.DELAY_HINT')"
                    class="min-h-11 w-full px-3 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-alpha-1 text-n-slate-12 text-center disabled:cursor-not-allowed disabled:opacity-60 sm:w-28"
                  />
                  <Select
                    id="pipeline-follow-up-delay-unit-select"
                    v-model="form.delay_unit"
                    input-id="pipeline-follow-up-delay-unit"
                    name="pipelineFollowUpDelayUnit"
                    class="min-w-0 flex-1 [&>select]:min-h-11 [&>select]:w-full"
                    :aria-label="t('FOLLOW_UP_RULES.FORM.DELAY_UNIT_LABEL')"
                    :options="delayUnitOptions"
                    :disabled="isSaving"
                  />
                </div>
                <p class="mt-1.5 text-xs text-n-slate-10">
                  {{ t('FOLLOW_UP_RULES.FORM.DELAY_HINT') }}
                </p>
              </div>

              <!-- Send Window -->
              <div class="mb-4">
                <SendWindowFields v-model="form.send_window" />
              </div>

              <!-- Enabled -->
              <div class="mb-4">
                <label class="flex min-h-11 items-center gap-3 cursor-pointer">
                  <input
                    v-model="form.enabled"
                    name="pipelineFollowUpEnabled"
                    type="checkbox"
                    :disabled="isSaving"
                    class="w-5 h-5 rounded border-n-weak text-n-brand focus:ring-n-brand focus:ring-offset-0"
                  />
                  <span class="text-sm font-medium text-n-slate-11">
                    {{ t('FOLLOW_UP_RULES.FORM.ENABLED') }}
                  </span>
                </label>
              </div>

              <!-- Important Notice -->
              <div class="p-3 bg-n-amber-3 border border-n-amber rounded-lg">
                <p class="text-xs text-n-amber-11">
                  <span class="font-semibold">
                    {{ t('FOLLOW_UP_RULES.FORM.NOTICE_TITLE') }}
                  </span>
                  {{ t('FOLLOW_UP_RULES.FORM.NOTICE_TEXT') }}
                </p>
              </div>

              <!-- Form Actions -->
              <div class="flex gap-3 mt-4 pt-4 border-t border-n-weak">
                <NextButton
                  type="button"
                  variant="faded"
                  color="slate"
                  size="md"
                  class="flex-1"
                  :disabled="isSaving"
                  @click="closeForm"
                >
                  {{ t('FOLLOW_UP_RULES.FORM.CANCEL') }}
                </NextButton>
                <NextButton
                  type="submit"
                  variant="solid"
                  color="blue"
                  size="md"
                  class="flex-1"
                  :is-loading="isSaving"
                  :disabled="isSaving"
                >
                  <template v-if="editingRule">
                    {{ t('FOLLOW_UP_RULES.FORM.UPDATE') }}
                  </template>
                  <template v-else>
                    {{ t('FOLLOW_UP_RULES.FORM.CREATE') }}
                  </template>
                </NextButton>
              </div>
            </div>
          </form>

          <!-- Empty State -->
          <div v-else-if="rules.length === 0" class="text-center py-8">
            <div
              class="w-12 h-12 rounded-full bg-n-alpha-3 flex items-center justify-center mx-auto mb-3"
            >
              <span class="i-lucide-zap size-6 text-n-slate-10" />
            </div>
            <h4 class="text-sm font-medium text-n-slate-12 mb-1">
              {{ t('FOLLOW_UP_RULES.EMPTY.TITLE') }}
            </h4>
            <p class="text-sm text-n-slate-11 max-w-sm mx-auto mb-4">
              {{ t('FOLLOW_UP_RULES.EMPTY.DESCRIPTION') }}
            </p>
            <NextButton
              variant="solid"
              color="blue"
              size="md"
              icon="add"
              @click="openCreateForm"
            >
              {{ t('FOLLOW_UP_RULES.EMPTY.CREATE_FIRST') }}
            </NextButton>
          </div>

          <!-- Rules List -->
          <div v-else class="space-y-3">
            <!-- Add Rule Button -->
            <div class="flex justify-end mb-1">
              <NextButton
                variant="faded"
                color="blue"
                size="md"
                icon="add"
                @click="openCreateForm"
              >
                {{ t('FOLLOW_UP_RULES.ADD_RULE') }}
              </NextButton>
            </div>

            <div
              v-for="rule in rules"
              :key="rule.id"
              class="p-4 border border-n-weak rounded-lg hover:border-n-slate-7 transition-colors"
            >
              <div
                class="flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-start"
              >
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-2">
                    <span
                      class="px-2 py-0.5 text-xs font-medium rounded-full"
                      :class="[
                        rule.enabled
                          ? 'bg-n-teal-3 text-n-teal-11'
                          : 'bg-n-alpha-3 text-n-slate-11',
                      ]"
                    >
                      {{
                        rule.enabled
                          ? t('FOLLOW_UP_RULES.ACTIVE')
                          : t('FOLLOW_UP_RULES.INACTIVE')
                      }}
                    </span>
                    <span class="text-sm font-medium text-n-slate-12">
                      {{ rule.follow_up_template_name }}
                    </span>
                  </div>

                  <div class="flex items-center gap-2 text-sm text-n-slate-11">
                    <span
                      v-if="rule.from_stage"
                      class="px-2 py-0.5 bg-n-alpha-3 rounded text-xs"
                    >
                      {{ getStageName(rule.from_stage) }}
                    </span>
                    <span v-else class="text-xs italic">{{
                      t('FOLLOW_UP_RULES.ANY_STAGE.LABEL')
                    }}</span>
                    <span class="i-lucide-arrow-right size-4" />
                    <span
                      class="px-2 py-0.5 bg-n-blue-3 text-n-blue-11 rounded text-xs font-medium"
                    >
                      {{ getStageName(rule.to_stage) }}
                    </span>
                  </div>

                  <p class="text-xs text-n-slate-10 mt-2">
                    {{
                      t('FOLLOW_UP_RULES.DELAY_SUMMARY', {
                        delay: getDelayDescription(rule),
                      })
                    }}
                  </p>
                  <SendWindowBadge
                    v-if="rule.send_window && rule.send_window.enabled"
                    :window="rule.send_window"
                    class="mt-1.5"
                  />
                </div>

                <div
                  class="flex items-center justify-end gap-1 sm:justify-start"
                >
                  <button
                    type="button"
                    class="flex size-11 items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                    :aria-label="
                      rule.enabled
                        ? t('FOLLOW_UP_RULES.STATUS.TOGGLE_INACTIVE')
                        : t('FOLLOW_UP_RULES.STATUS.TOGGLE_ACTIVE')
                    "
                    :disabled="togglingId !== null || deletingId !== null"
                    @click="toggleEnabled(rule)"
                  >
                    <span
                      :class="
                        togglingId === rule.id
                          ? 'i-svg-spinners-90-ring-with-bg text-n-brand'
                          : rule.enabled
                            ? 'i-lucide-toggle-right text-n-teal-9'
                            : 'i-lucide-toggle-left'
                      "
                      class="size-5"
                    />
                  </button>
                  <button
                    type="button"
                    class="flex size-11 items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                    :aria-label="t('FOLLOW_UP_RULES.EDIT')"
                    :disabled="togglingId !== null || deletingId !== null"
                    @click="openEditForm(rule)"
                  >
                    <span class="i-lucide-pencil size-4" />
                  </button>
                  <button
                    type="button"
                    class="flex size-11 items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-ruby-3 hover:text-n-ruby-9 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                    :aria-label="t('FOLLOW_UP_RULES.LIST.DELETE')"
                    :disabled="togglingId !== null || deletingId !== null"
                    @click="handleDelete(rule)"
                  >
                    <span
                      :class="
                        deletingId === rule.id
                          ? 'i-svg-spinners-90-ring-with-bg'
                          : 'i-lucide-trash-2'
                      "
                      class="size-4"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </Modal>

  <ConfirmModal
    ref="discardConfirmation"
    :title="t('FOLLOW_UP_RULES.DISCARD.TITLE')"
    :description="t('FOLLOW_UP_RULES.DISCARD.DESCRIPTION')"
    :confirm-label="t('FOLLOW_UP_RULES.DISCARD.CONFIRM')"
    :cancel-label="t('FOLLOW_UP_RULES.DISCARD.KEEP_EDITING')"
  />

  <ConfirmModal
    ref="deleteConfirmation"
    :title="t('FOLLOW_UP_RULES.DELETE.TITLE')"
    :description="
      t('FOLLOW_UP_RULES.DELETE.MESSAGE_WITH_NAME', {
        name: pendingDelete?.follow_up_template_name,
      })
    "
    :confirm-label="t('FOLLOW_UP_RULES.DELETE.CONFIRM')"
    :cancel-label="t('FOLLOW_UP_RULES.DELETE.CANCEL_BUTTON')"
  />
</template>
